import {
  TROPHY_TIER_IDS,
  type GamificationTrophyItem,
  type TrophyId,
} from '@crowdfpl/domain/gamification';
import { type ReactElement } from 'react';
import {
  formatTrophyTitle,
  TROPHY_ACCENT,
  TROPHY_EMOJI,
  TROPHY_METRIC_UNIT,
} from '@/components/gamification/trophy-copy';
import { TrophyProgressLadder } from '@/components/gamification/trophy-progress-ladder';

type AlmostThereSectionProps = {
  trophies: readonly GamificationTrophyItem[];
  isOwnProfile?: boolean;
  pinnedTrophyId?: TrophyId | null;
  onPin?: (trophyId: TrophyId) => void;
  isPinPending?: boolean;
};

function AlmostThereCard({ trophy }: { trophy: GamificationTrophyItem }): ReactElement {
  const accent = TROPHY_ACCENT[trophy.trophy_id];
  const pct = Math.round((trophy.next_progress ?? 0) * 100);
  // One-shot trophies have no tiers — never say "70% to Bronze".
  const progressLabel =
    trophy.kind === 'multi_tier'
      ? (() => {
          const nextIndex =
            trophy.highest_tier_index == null
              ? 0
              : Math.min(trophy.highest_tier_index + 1, TROPHY_TIER_IDS.length - 1);
          const nextTier = TROPHY_TIER_IDS[nextIndex];
          const nextLabel = nextTier.charAt(0).toUpperCase() + nextTier.slice(1);
          return `${pct}% to ${nextLabel}`;
        })()
      : `${pct}% complete`;
  const unit = TROPHY_METRIC_UNIT[trophy.trophy_id];
  const progressLine =
    trophy.next_target != null
      ? `${trophy.metric_value} / ${trophy.next_target}${unit ? ` ${unit}` : ''}`
      : null;

  return (
    <div
      className="relative flex flex-col gap-2 overflow-hidden rounded-xl border bg-card p-3"
      style={{ borderColor: `color-mix(in srgb, ${accent} 33%, transparent)` }}
      data-testid={`almost-there-card-${trophy.trophy_id}`}
      data-gamification-motion="decorative"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-30 blur-3xl"
        style={{ background: accent }}
      />
      <div className="relative flex items-start gap-2.5">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl"
          style={{
            background: `color-mix(in srgb, ${accent} 13%, transparent)`,
            boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 33%, transparent) inset`,
          }}
          aria-hidden
        >
          {TROPHY_EMOJI[trophy.trophy_id]}
        </div>
        <div className="min-w-0 flex-1">
          {/* Accent on text fails WCAG on light theme (gold ~2:1); keep accent on chrome only. */}
          <div className="text-[11px] font-bold uppercase tracking-wider text-foreground">
            {progressLabel}
          </div>
          <div className="truncate text-[13px] font-bold leading-tight text-foreground">
            {formatTrophyTitle(trophy.trophy_id)}
          </div>
          {progressLine ? (
            <div className="truncate text-[11px] tabular-nums leading-snug text-muted-foreground">
              {progressLine}
            </div>
          ) : null}
        </div>
      </div>
      {trophy.kind === 'multi_tier' ? (
        <TrophyProgressLadder
          highestTierIndex={trophy.highest_tier_index}
          isMaxed={trophy.status === 'maxed'}
          nextProgress={trophy.next_progress}
          compact
        />
      ) : null}
    </div>
  );
}

/**
 * Highlight strip for near-complete trophies. Hidden when the candidate list is empty.
 */
export function AlmostThereSection({
  trophies,
}: AlmostThereSectionProps): ReactElement | null {
  if (trophies.length === 0) {
    return null;
  }

  const visible = trophies.slice(0, 3);

  return (
    <section aria-label="Almost There" data-testid="almost-there-section" className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">Almost There</h3>
      <div className="grid gap-2 sm:grid-cols-3">
        {visible.map(trophy => (
          <AlmostThereCard key={trophy.trophy_id} trophy={trophy} />
        ))}
      </div>
    </section>
  );
}
